<script setup>
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { CustomAttributionControl } from '@/utils/customAttribution'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  lat: {
    type: Number,
    required: true
  },
  lon: {
    type: Number,
    required: true
  },
  direction: {
    type: Number,
    required: true
  },
  name: {
    type: String,
    default: ''
  },
  tenkiLink: {
    type: Object,
    default: () => ({})
  }
})
const { data: fcstobs } = await useFetch(computed(() => `/api/wxdata/fcstobs?lon=${props.lon}&lat=${props.lat}`))
const config = useRuntimeConfig()
const map = shallowRef(null)
const mapContainer = ref(null)
const initMap = async (center) => {
  let attributionControl = null
  const protocol = new Protocol()
  maplibregl.addProtocol('pmtiles', protocol.tile)
  map.value = new maplibregl.Map({
    container: mapContainer.value,
    style: `${config.public.baseMapDomain}/style/global/osm/basic-ja.json`,
    localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
    center: center,
    zoom: 7,
    attributionControl: false,
    boxZoom: false,
    doubleClickZoom: false,
    dragPan: false,
    dragRotate: false,
    keyboard: false,
    scrollZoom: false,
    touchPitch: false,
    touchZoomRotate: false
  })
  attributionControl = new CustomAttributionControl({
    compact: true
  })
  map.value.addControl(attributionControl, 'bottom-left')
  map.value.once('load', () => {
    const osmSource = map.value.getSource('osm')
    const attribution = osmSource.attribution
    if (attribution) {
      attributionControl.updateCustomAttributions([attribution])
    }
  })
}
onMounted(async () => {
  await initMap([props.lon, props.lat])
})
</script>

<template>
  <div class="livecamInfo">
    <div class="content">
      <figure class="image">
        <div
          ref="mapContainer"
          class="map"
        />
        <div
          class="icon"
          :style="`transform: rotate(${props.direction * 22.5 + 180}deg); transform-origin: 13px 10px;`"
        >
          <img
            src="https://gvs.weathernews.jp/onebox/img/livecam/livecam.svg"
            alt=""
          >
        </div>
        <p class="date">
          {{ dayjs.unix(fcstobs.time).tz('Asia/Tokyo').format('HH:mm') }} 現在
        </p>
      </figure>
      <div class="table">
        <div
          v-for="item in fcstobs.list"
          :key="item.name"
          class="row"
        >
          <p class="head">
            {{ item.name }}
          </p>
          <div class="body">
            <figure
              v-if="item.icon"
              class="weather"
            >
              <img
                :src="`https://gvs.weathernews.jp/onebox/img/wxicon/${item.icon}.webp`"
                loading="lazy"
                alt=""
              >
            </figure>
            <figure
              v-if="item.dir"
              class="wind"
            >
              <svg
                v-if="item.rank!==0"
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="15"
                viewBox="0 0 11 15"
                :data-rank="item.rank"
                :style="`transform: rotate(calc(1deg * ${item.dir*22.5}))`"
              >
                <path
                  d="M9.91706 1.33286L5.48529 13.7418L1.05353 1.33286L5.31805 5.17093L5.48529 5.32145L5.65253 5.17093L9.91706 1.33286Z"
                  stroke="#333333"
                  stroke-width="0.5"
                />
              </svg>
            </figure>
            <p class="value">
              {{ item.value }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="tenkiLink.text"
      class="button"
    >
      <CommonMoreButton
        :text="`${tenkiLink.text}周辺の天気予報`"
        direction="left"
        col="dark-blue"
        :link="tenkiLink.url"
        :external="true"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.livecamInfo {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .content {
    display: flex;
    .image {
      position: relative;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 254px;
      .map {
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
      .icon {
        position: absolute;
        top: calc(50% - (13px * 75 / 52) + 9px);
        left: calc(50% - 13px);
        width: 26px;
        aspect-ratio: 52 / 75;
        img {
          width: 100%;
          height: 100%;
        }
      }
      .date {
        position: absolute;
        top: 4px;
        right: 4px;
        padding: 5px;
        border-radius: 3px;
        background-color: #fff;
        color: #303030;
        text-align: center;
        font-size: 10px;
        font-weight: 600;
        line-height: 1em;
      }
    }
    .table {
      flex: 1;
      display: flex;
      flex-direction: column;
      max-width: 300px;
      gap: 2px;
      .row {
        display: flex;
        .head {
          width: 70px;
          height: 30px;
          background-color: #EEF1F8;
          padding: 8px 10px;
          font-size: 14px;
          font-weight: 600;
          line-height: 1em;
        }
        .body {
          display: flex;
          align-items: center;
          flex: 1;
          gap: 5px;
          height: 30px;
          padding: 8px 10px;
          border-bottom: solid 1px #EEF1F8;
          .weather {
            height: 15px;
            aspect-ratio: 19 / 14;
            img {
              height: 100%;
              vertical-align: top;
            }
          }
          .wind {
            width: 15px;
            height: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            svg {
              &[data-rank='0'] {
              fill: #ffffff;
              }
              &[data-rank='1'] {
                fill: #03aeef;
              }
              &[data-rank='2'] {
                fill: #05ad50;
              }
              &[data-rank='3'] {
                fill: #febf00;
              }
              &[data-rank='4'] {
                fill: #e86200;
              }
              &[data-rank='5'] {
                fill: #fe0000;
              }
            }
          }
          .value {
            font-size: 14px;
            line-height: 1em;
          }
        }
      }
    }
  }
  .button {
    margin-left: auto;
  }
}
@media screen and (min-width: 830px) {
  .livecamInfo {
    .content {
      display: flex;
      gap: 10px;
      .image {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 146px;
        width: 40%;
        min-width: calc(100% - 362px);
        max-width: none;
        object-fit: cover;
        vertical-align: bottom;
      }
      .table {
        flex-direction: row;
        flex-wrap: wrap;
        min-width: 352px;
        width: 60%;
        box-sizing: border-box;
        .row {
          width: calc(50% - 1px);
          box-sizing: border-box;
          .head {
            min-width: 65px;
            width: 40%;
            height: 35px;
            padding: 10px;
            font-size: 15px;
          }
          .body {
            height: 35px;
            width: 60%;
            min-width: 110px;
            padding: 10px;
            .value {
              font-size: 15px;
            }
          }
        }
      }
    }
  }
}
</style>
